/**
 * Zod boundary schemas + response types for reported Issues (src/api/support-issues.ts).
 *
 * The submit is ONE multipart request: a `data` field holding the JSON below, plus up to
 * three image parts — `screenshot` (marked up, or plain), `original` (un-marked, only when
 * marks were added and nothing was redacted) and `delyverImage` (the copy sized for
 * Delyver's 2 MB / 1,600 px limit). One request means one press makes exactly one Issue.
 */

import { z } from "zod";
import {
  SUPPORT_ISSUE_CATEGORIES,
  SUPPORT_ISSUE_PRIORITIES,
  SUPPORT_ISSUE_STATUSES,
  type SupportIssueCategory,
  type SupportIssuePriority,
  type SupportIssueStatus,
} from "@/lib/support-issue-labels";

/** Caps on what the browser may send. The server re-caps after redacting. */
export const CONSOLE_TRAIL_MAX = 50;
export const CONSOLE_ENTRY_MAX_CHARS = 500;
export const NETWORK_TRAIL_MAX = 30;
export const NETWORK_ENTRY_MAX_CHARS = 200;
export const SCREENSHOT_NOTES_MAX = 50;

/** Per image part. Three parts stay under the server's 10 MB request cap. */
export const SCREENSHOT_MAX_BYTES = 3_500_000;
/** Delyver's own limit is 2 MB; leave headroom for base64 + JSON. */
export const DELYVER_IMAGE_MAX_BYTES = 1_900_000;
export const DELYVER_IMAGE_MAX_EDGE = 1600;
/** Raster only — an SVG is a script carrier. */
export const SCREENSHOT_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

const consoleEntrySchema = z.object({
  level: z.enum(["error", "warn"]),
  // Generous caps here; the server redacts, then trims to the per-entry limit.
  message: z.string().max(5000),
  at: z.string(),
});

const networkEntrySchema = z.object({
  kind: z.enum(["fetch", "navigation"]),
  method: z.string().max(10).optional(),
  path: z.string().max(2000),
  status: z.number().int().optional(),
  durationMs: z.number().int().min(0).optional(),
  at: z.string(),
});

export type ConsoleTrailEntry = z.infer<typeof consoleEntrySchema>;
export type NetworkTrailEntry = z.infer<typeof networkEntrySchema>;

export const createSupportIssueSchema = z.object({
  clientSubmissionId: z.uuid(),
  title: z.string().trim().min(1, "Give the issue a title").max(200),
  description: z.string().trim().min(1, "Say what happened").max(5000),
  category: z.enum(SUPPORT_ISSUE_CATEGORIES).nullable().default(null),
  priority: z.enum(SUPPORT_ISSUE_PRIORITIES).default("normal"),
  context: z
    .object({
      pageUrl: z.string().max(2000).optional(),
      route: z.string().max(500).optional(),
      userAgent: z.string().max(500).optional(),
      viewport: z.string().max(40).optional(),
      appVersion: z.string().max(100).optional(),
    })
    .default({}),
  consoleTrail: z.array(consoleEntrySchema).max(CONSOLE_TRAIL_MAX).default([]),
  networkTrail: z.array(networkEntrySchema).max(NETWORK_TRAIL_MAX).default([]),
  screenshot: z
    .object({
      markedUp: z.boolean().default(false),
      redacted: z.boolean().default(false),
      notes: z.array(z.string().max(500)).max(SCREENSHOT_NOTES_MAX).default([]),
    })
    .default({ markedUp: false, redacted: false, notes: [] }),
});

export type CreateSupportIssueInput = z.infer<typeof createSupportIssueSchema>;

export const supportIssueListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursor: z.string().optional(),
  direction: z.enum(["next", "prev"]).default("next"),
  /** `mine`: the actor's own Issues. `queue`: every Issue (ADMIN). */
  scope: z.enum(["mine", "queue"]).default("mine"),
  /** `active` (default), `all`, or one status. */
  status: z.enum(["active", "all", ...SUPPORT_ISSUE_STATUSES]).default("active"),
  /** `me`, `unassigned`, or omitted for anyone. Queue only. */
  owner: z.enum(["me", "unassigned"]).optional(),
  search: z.string().trim().max(200).optional(),
});

export const updateSupportIssueSchema = z
  .object({
    status: z.enum(SUPPORT_ISSUE_STATUSES).optional(),
    priority: z.enum(SUPPORT_ISSUE_PRIORITIES).optional(),
    /** An ADMIN's user id, or null for nobody. */
    ownerUserId: z.string().min(1).nullable().optional(),
  })
  .refine(
    (b) => b.status !== undefined || b.priority !== undefined || b.ownerUserId !== undefined,
    { message: "Nothing to change" },
  );

export const commentOnSupportIssueSchema = z.object({
  body: z.string().trim().min(1, "Write a comment").max(5000),
});

// ── Responses ────────────────────────────────────────────────────────────────

export interface SupportIssueListItem {
  id: string;
  code: string;
  title: string;
  category: SupportIssueCategory | null;
  priority: SupportIssuePriority;
  status: SupportIssueStatus;
  route: string | null;
  reporterName: string;
  reporterEmail: string;
  reporterRole: string;
  ownerUserId: string | null;
  ownerName: string | null;
  hasScreenshot: boolean;
  createdAt: string;
}

export interface SupportIssueListResponse {
  data: SupportIssueListItem[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface SupportIssueTimelineEntry {
  id: string;
  kind: "created" | "status" | "priority" | "owner" | "comment";
  actorName: string | null;
  /** A comment's text, or a one-line description of the change. */
  text: string;
  at: string;
}

export interface SupportIssueDiagnostics {
  pageUrl: string | null;
  userAgent: string | null;
  viewport: string | null;
  appVersion: string | null;
  consoleTrail: ConsoleTrailEntry[];
  networkTrail: NetworkTrailEntry[];
}

export interface SupportIssueDelyverSync {
  status: "pending" | "sent" | "failed" | "skipped";
  shortId: string | null;
  attempts: number;
  lastError: string | null;
  syncedAt: string | null;
}

export interface SupportIssueDetail extends SupportIssueListItem {
  description: string;
  updatedAt: string;
  resolvedAt: string | null;
  screenshot: {
    state: "none" | "stored" | "delyver_only";
    markedUp: boolean;
    redacted: boolean;
    notes: string[];
    /** ADMIN only, and only when an un-marked original was kept. */
    hasOriginal: boolean;
  };
  timeline: SupportIssueTimelineEntry[];
  /** ADMIN only: page, browser, console and network trails. */
  diagnostics: SupportIssueDiagnostics | null;
  /** ADMIN only. */
  delyver: SupportIssueDelyverSync | null;
  /** The actor works the queue: may change status, priority and owner. */
  canWork: boolean;
  canComment: boolean;
  canClose: boolean;
}

export interface SupportIssueCreateResponse {
  issue: SupportIssueDetail;
  /** False when a retried submit found the Issue the first press already made. */
  created: boolean;
}

export interface SupportIssueAssignee {
  id: string;
  name: string;
  email: string;
}
